/**
 * Coalesce a burst of live-stream triggers into at most one call per window.
 *
 * The positions stream publishes up to every 2 s and used to invalidate the
 * heaviest Monitoring queries on every frame, with no throttle. A coalescer
 * fires at once on the leading edge when the window since the last fire has
 * passed; otherwise it schedules ONE trailing fire at `lastFire + window`, and
 * any further trigger inside that window collapses into it. So a change is
 * shown immediately, a steady stream costs one refetch per window, and the
 * last change of a burst is never dropped.
 *
 * `cancel()` clears the pending fire and disposes the coalescer (an unmounted
 * hook must never fire). A `fire` that throws is reported and does not stop
 * later fires: the state is updated before the call and the error is caught.
 *
 * Pure: no imports; the clock is injectable for tests.
 */

export interface Clock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface Coalescer {
  trigger(): void;
  cancel(): void;
  /** How many times `fire` has been called. */
  readonly fires: number;
}

const realClock: Clock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>),
};

export function createCoalescer(windowMs: number, fire: () => void, clock: Clock = realClock): Coalescer {
  if (!Number.isFinite(windowMs) || windowMs < 0) throw new RangeError(`coalescer window must be >= 0 ms, got ${windowMs}`);
  let lastFire = Number.NEGATIVE_INFINITY;
  let timer: unknown = null;
  let pending = false;
  let disposed = false;
  let fires = 0;

  const run = () => {
    lastFire = clock.now();
    fires += 1;
    try {
      fire();
    } catch (error) {
      // One failed refresh must not silence the next ones.
      console.error('live refresh failed', error);
    }
  };

  return {
    trigger() {
      if (disposed || pending) return;
      const now = clock.now();
      if (now - lastFire >= windowMs) {
        run();
        return;
      }
      pending = true;
      timer = clock.setTimeout(() => {
        timer = null;
        pending = false;
        if (!disposed) run();
      }, Math.max(0, lastFire + windowMs - now));
    },
    cancel() {
      disposed = true;
      if (pending) clock.clearTimeout(timer);
      timer = null;
      pending = false;
    },
    get fires() {
      return fires;
    },
  };
}
